import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9li51rh5tg6sock7b0";

type Status = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const topics = ["内容建议", "资源纠错", "合作咨询", "其他留言"];

export default function MessageForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const honeypot = String(formData.get("company_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim()) payload.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMessage === "string" && serverMessage.trim()
            ? serverMessage
            : "提交失败，请检查填写内容后重试。",
        );
        return;
      }

      setStatus("success");
      formElement.reset();
    } catch {
      setStatus("error");
      setFormError("网络连接异常，留言未能提交，请稍后再试。");
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
      <h2 className="font-heading text-xl font-bold text-foreground-950">给我们留言</h2>
      <p className="mt-2 text-xs leading-relaxed text-foreground-500">
        对内容有建议、发现资料错误，或想聊聊合作，都可以在这里留言。我们会尽快查看。
      </p>

      {status === "success" ? (
        <div className="mt-5 rounded-md border border-accent-200 bg-accent-50 p-5 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-50 text-accent-600">
            <i className="ri-check-line text-2xl" />
          </span>
          <p className="mt-3 text-sm font-medium text-accent-900">留言已提交</p>
          <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
            感谢你的反馈，我们已收到这条留言。
          </p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="mt-4 inline-flex h-9 cursor-pointer items-center rounded-md border border-background-300 bg-background-50 px-4 text-xs text-foreground-700"
          >
            再写一条
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} id="message-form" data-readdy-form className="mt-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="msg-name" className="mb-1.5 block text-xs text-foreground-700">
                称呼
              </label>
              <input
                id="msg-name"
                name="name"
                type="text"
                required
                maxLength={40}
                placeholder="怎么称呼你"
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="msg-email" className="mb-1.5 block text-xs text-foreground-700">
                邮箱
              </label>
              <input
                id="msg-email"
                name="email"
                type="email"
                required
                placeholder="用于接收回复"
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label htmlFor="msg-topic" className="mb-1.5 block text-xs text-foreground-700">
              留言类型
            </label>
            <select
              id="msg-topic"
              name="topic"
              className="h-10 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
            >
              {topics.map((topic) => (
                <option key={topic} value={topic}>
                  {topic}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="msg-content" className="mb-1.5 block text-xs text-foreground-700">
              留言内容
            </label>
            <textarea
              id="msg-content"
              name="content"
              required
              maxLength={500}
              rows={5}
              placeholder="写下你的想法（500 字以内）"
              className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>

          <input
            className="form-guard-field"
            type="text"
            name="company_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          {status === "error" && (
            <p className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-xs leading-relaxed text-primary-800">
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-8"
          >
            {status === "submitting" ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                提交中
              </>
            ) : (
              <>
                提交留言
                <i className="ri-send-plane-line" />
              </>
            )}
          </button>
          <p className="text-[11px] leading-relaxed text-foreground-400">
            提交即表示你了解本站为演示项目，影视资料均为虚构内容。
          </p>
        </form>
      )}
    </div>
  );
}